import React, { useEffect, useRef } from "react";
import { pdfjs } from "react-pdf";
import DocumentViewer from "./components/DocumentViewer";
import SignatureModal from "./components/SignatureModal";
import { useSignature } from "./hooks/useSignature";
import { usePDF } from "./hooks/usePDF";
import "./signature.css";

// Configure PDF.js worker
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const SignPdf = ({
  pdfUrl = "",
  signaturePositions: initialSignaturePositions = [],
  onSigningComplete,
}) => {
  const containerRef = useRef(null);

  const {
    isModalOpen,
    setIsModalOpen,
    setSelectedPosition,
    isSavingSignature,
    signatureRef,
    clearSignature,
    saveSignature,
    handleSignatureUpload,
    setSignaturePositions,
    signaturePositions,
  } = useSignature();

  useEffect(() => {
    setSignaturePositions(initialSignaturePositions);
  }, [initialSignaturePositions]);

  const {
    scale,
    setScale,
    currentPage,
    setCurrentPage,
    numPages,
    onDocumentLoadSuccess,
    generateSignedPdf,
  } = usePDF();

  // Handle signature button click
  const handleSignatureClick = (position) => {
    if (!position.signature) {
      setSelectedPosition(position);
      setIsModalOpen(true);
    }
  };

  // Handle signature removal - only remove the signature while keeping the position
  const handleRemoveSign = (index) => {
    const updatedPositions = signaturePositions.map((pos, i) => {
      if (i === index) {
        // Keep all position data but remove the signature and timestamp
        const { signature, timestamp, ...rest } = pos;
        return rest;
      }
      return pos;
    });
    setSignaturePositions(updatedPositions);
  };

  // Handle PDF signing completion
  const handleSigningComplete = async () => {
    try {
      const signedPdf = await generateSignedPdf(pdfUrl, signaturePositions);
      if (onSigningComplete) onSigningComplete(signedPdf, signaturePositions);
    } catch (error) {
      console.error("Error generating signed PDF:", error);
    }
  };

  useEffect(() => {
    if (signaturePositions?.length) handleSigningComplete();
  }, [signaturePositions]);

  const handleUploadClick = () => {
    setSelectedPosition("UPLOAD_ALL");
    setIsModalOpen(true);
  };

  return (
    <div className="ps-wrapper">
      <DocumentViewer
        pdfUrl={pdfUrl}
        signaturePositions={signaturePositions}
        onSignatureClick={handleSignatureClick}
        onDocumentLoadSuccess={onDocumentLoadSuccess}
        currentPage={currentPage}
        scale={scale}
        setCurrentPage={setCurrentPage}
        setScale={setScale}
        containerRef={containerRef}
        numPages={numPages}
        onRemoveSign={handleRemoveSign}
        handleUploadClick={handleUploadClick}
      />

      <SignatureModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPosition(null);
        }}
        isSavingSignature={isSavingSignature}
        signatureRef={signatureRef}
        clearSignature={clearSignature}
        onSave={saveSignature}
        onUpload={handleSignatureUpload}
      />
    </div>
  );
};

export default SignPdf;
